import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Button } from '../button';

export interface FormActionBarProps {
  /** Zavře okno / odejde zpět (bez ukládání). */
  onBack: () => void;
  /** Uloží objekt. Vrací (nebo resolvuje na) `true` při úspěchu – „Uložit & zpět“ podle toho zavře okno. */
  onSave: () => boolean | Promise<boolean>;
  /** Probíhá ukládání – tlačítka jsou zablokovaná a „Uložit“ ukazuje spinner. */
  saving?: boolean;
  /** Zakáže ukládací akce (např. neplatný formulář). */
  disabled?: boolean;
  /** Volitelný obsah vlevo (stav uložení apod.). */
  statusNote?: ReactNode;
  className?: string;
}

/**
 * Sdílená spodní lišta formuláře s běžnými akcemi **Uložit & zpět · Zpět · Uložit** (parita s legacy agendami Magic2G).
 *
 * - **Zpět** zavře okno (`onBack`).
 * - **Uložit** uloží a zůstane v okně.
 * - **Uložit & zpět** uloží a teprve po úspěšném uložení (`onSave` vrátí `true`) zavře okno.
 */
export function FormActionBar({
  onBack,
  onSave,
  saving = false,
  disabled = false,
  statusNote,
  className,
}: FormActionBarProps) {
  const { t } = useTranslation('sharedUi');

  const handleSave = () => {
    void Promise.resolve(onSave());
  };

  const handleSaveAndBack = async () => {
    const saved = await Promise.resolve(onSave());
    if (saved) {
      onBack();
    }
  };

  return (
    <footer
      className={cn(
        'bg-phx-surface-2 border-phx-border-2 px-phx-5 gap-phx-4 flex shrink-0 items-center border-t py-[8px]',
        className
      )}
    >
      {statusNote ? (
        <span className="text-phx-text-muted text-phx-sm inline-flex min-w-0 items-center">
          {statusNote}
        </span>
      ) : null}
      <span className="flex-1" />
      <div className="gap-phx-2 flex items-center">
        <Button variant="ghost" size="sm" onClick={onBack} disabled={saving}>
          {t('formActionBar.back')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => void handleSaveAndBack()}
          loading={saving}
          disabled={disabled}
        >
          {t('formActionBar.saveAndBack')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSave}
          loading={saving}
          disabled={disabled}
        >
          {t('formActionBar.save')}
        </Button>
      </div>
    </footer>
  );
}
